import { useId, useState, type FormEvent } from 'react';
import { apiErrorText } from '@shared/api';
import { parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import { formatPeriodMonthLabel } from './inflowHelpers';
import {
  checkPlanForm,
  periodOf,
  type PlanErrors,
  type PlanField,
  type PlanForm,
  type PlanPayload,
} from './model';
import {
  ButtonGroup,
  ErrorMessage,
  FieldError,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FormBox,
  FormGrid,
  FormTitle,
  Input,
} from './styles';

interface AddInflowPlanModalProps {
  /** Дата оплаты по умолчанию: подсказка из выбранного периода, иначе сегодня. */
  defaultExpectedDate: string;
  isCreating: boolean;
  /** Сохранить строку; ошибка запроса пробрасывается — форма покажет её и не очистится. */
  onCreate: (payload: PlanPayload) => Promise<void>;
  onClose: () => void;
}

/** Поля формы: подпись и что вводится. Суммы — текстом, их разбирает `parseMoney`. */
const FIELDS: ReadonlyArray<{
  field: PlanField;
  label: string;
  placeholder?: string;
  money?: boolean;
  date?: boolean;
}> = [
  { field: 'projectName', label: 'Строительный объект *' },
  { field: 'customerName', label: 'Заказчик *' },
  { field: 'contractNo', label: '№ договора подряда' },
  { field: 'expectedDate', label: 'Ожидаемая дата оплаты', date: true },
  { field: 'bdds', label: 'План по БДДС, ₽ *', placeholder: '0, если вне плана', money: true },
  { field: 'ks3', label: 'Подписано по КС-3 (брутто), ₽', placeholder: 'Нет — оставьте пустым', money: true },
  { field: 'ks3DocNo', label: '№ справки КС-3', placeholder: 'Например, КС-3 № 15' },
  { field: 'advance', label: 'Зачёт аванса по КС-3, ₽', placeholder: 'Нет — оставьте пустым', money: true },
  { field: 'retention', label: 'Гарантийное удержание (ГУ), ₽', placeholder: 'Нет — оставьте пустым', money: true },
  { field: 'actual', label: 'Фактически поступило на р/с, ₽', placeholder: 'Нет — оставьте пустым', money: true },
  { field: 'responsible', label: 'Ответственный РП' },
];

export function AddInflowPlanModal({
  defaultExpectedDate,
  isCreating,
  onCreate,
  onClose,
}: AddInflowPlanModalProps) {
  const uid = useId();
  const id = (field: PlanField) => `${uid}-${field}`;
  const [form, setForm] = useState<PlanForm>(() => ({
    projectName: '',
    customerName: '',
    contractNo: '',
    expectedDate: defaultExpectedDate,
    bdds: '',
    ks3: '',
    ks3DocNo: '',
    advance: '',
    retention: '',
    actual: '',
    responsible: '',
  }));
  const [errors, setErrors] = useState<PlanErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setField = (field: PlanField, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isCreating) return;
    const checked = checkPlanForm(form, parseMoney);
    setErrors(checked.errors);
    if (!checked.payload) {
      const first = FIELDS.find(({ field }) => checked.errors[field]);
      if (first) document.getElementById(id(first.field))?.focus();
      return;
    }
    try {
      setSubmitError(null);
      await onCreate(checked.payload);
    } catch (err) {
      setSubmitError(apiErrorText(err, 'Не удалось сохранить позицию БДДС / КС-3.'));
    }
  }

  const period = periodOf(form.expectedDate);

  return (
    <FormBox as="form" onSubmit={handleSubmit} noValidate aria-labelledby={`${uid}-title`}>
      <FormTitle id={`${uid}-title`}>
        Добавление позиции плана поступлений БДДС и подписанной КС-3
      </FormTitle>
      <FormGrid>
        {FIELDS.map(({ field, label, placeholder, money, date }) => {
          const error = errors[field];
          return (
            <FieldGroup key={field}>
              <FieldLabel htmlFor={id(field)}>{label}</FieldLabel>
              <Input
                id={id(field)}
                type={date ? 'date' : 'text'}
                inputMode={money ? 'decimal' : undefined}
                autoComplete="off"
                placeholder={placeholder}
                value={form[field]}
                onChange={(e) => setField(field, e.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={
                  error ? `${id(field)}-error` : date ? `${id(field)}-hint` : undefined
                }
              />
              {error && (
                <FieldError id={`${id(field)}-error`} role="alert">
                  {error}
                </FieldError>
              )}
              {date && !error && (
                <FieldHint id={`${id(field)}-hint`}>
                  {period
                    ? `Период БДДС: ${formatPeriodMonthLabel(period)}`
                    : 'Без даты строка попадёт в текущий месяц'}
                </FieldHint>
              )}
            </FieldGroup>
          );
        })}
      </FormGrid>
      {submitError && <ErrorMessage role="alert">{submitError}</ErrorMessage>}
      <ButtonGroup>
        <Button $size="sm" type="submit" disabled={isCreating}>
          {isCreating ? 'Сохраняю…' : 'Сохранить позицию'}
        </Button>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          Отмена
        </Button>
      </ButtonGroup>
    </FormBox>
  );
}
